import React, { useState, useEffect, useCallback } from 'react';
import DashboardLayout from '../../components/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  CheckCircle,
  XCircle,
  Edit3,
  Clock,
  AlertTriangle,
  Image as ImageIcon,
  DollarSign,
  User,
  Loader2,
  RefreshCcw,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../hooks/use-toast';

const AdApprovalsPage = () => {
  const { apiCall } = useAuth();
  const { toast } = useToast();

  // State
  const [approvals, setApprovals] = useState({ pending_count: 0, overdue_count: 0, campaigns: [] });
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [processing, setProcessing] = useState(null);

  // Dialog states
  const [rejectDialog, setRejectDialog] = useState({ open: false, campaign: null });
  const [editDialog, setEditDialog] = useState({ open: false, campaign: null });
  const [approveConfirm, setApproveConfirm] = useState({ open: false, campaign: null });

  // Form state
  const [rejectReason, setRejectReason] = useState('');
  const [rejectFeedback, setRejectFeedback] = useState('');
  const [editFeedback, setEditFeedback] = useState('');
  const [requiredChanges, setRequiredChanges] = useState('');

  const fetchApprovals = useCallback(async () => {
    try {
      setLoading(true);
      const params = statusFilter !== 'all' ? `?status_filter=${statusFilter}` : '';
      const data = await apiCall(`/api/admin/marketing/ad-approvals${params}`);
      setApprovals(data);
    } catch (error) {
      console.error('Failed to fetch approvals:', error);
      toast({
        title: 'Error',
        description: 'Failed to load approval queue',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }, [apiCall, statusFilter, toast]);

  useEffect(() => {
    fetchApprovals();
  }, [fetchApprovals]);

  // SLA countdown timer
  useEffect(() => {
    const interval = setInterval(() => {
      setApprovals(prev => ({
        ...prev,
        campaigns: prev.campaigns.map(c => ({
          ...c,
          hours_remaining: c.hours_remaining - (1 / 60), // Update every minute
          is_overdue: c.hours_remaining <= 0
        }))
      }));
    }, 60000); // Every minute

    return () => clearInterval(interval);
  }, []);

  const handleApprove = async (campaign) => {
    try {
      setProcessing(campaign.id);
      await apiCall(`/api/admin/marketing/ad-approvals/${campaign.id}/approve`, {
        method: 'POST',
      });
      toast({
        title: 'Campaign Approved',
        description: `Campaign "${campaign.campaign_name}&quot; has been approved`,
      });
      setApproveConfirm({ open: false, campaign: null });
      fetchApprovals();
    } catch (error) {
      console.error('Failed to approve:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to approve campaign',
        variant: 'destructive',
      });
    } finally {
      setProcessing(null);
    }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) {
      toast({
        title: 'Error',
        description: 'Please provide a rejection reason',
        variant: 'destructive',
      });
      return;
    }

    const campaign = rejectDialog.campaign;
    try {
      setProcessing(campaign.id);
      await apiCall(`/api/admin/marketing/ad-approvals/${campaign.id}/reject`, {
        method: 'POST',
        body: JSON.stringify({
          reason: rejectReason,
          feedback: rejectFeedback || null,
        }),
      });
      toast({
        title: 'Campaign Rejected',
        description: `Campaign rejected and ${campaign.budget} credits refunded to vendor`,
      });
      setRejectDialog({ open: false, campaign: null });
      setRejectReason('');
      setRejectFeedback('');
      fetchApprovals();
    } catch (error) {
      console.error('Failed to reject:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to reject campaign',
        variant: 'destructive',
      });
    } finally {
      setProcessing(null);
    }
  };

  const handleRequestEdit = async () => {
    if (!editFeedback.trim()) {
      toast({
        title: 'Error',
        description: 'Please provide feedback',
        variant: 'destructive',
      });
      return;
    }

    const campaign = editDialog.campaign;
    try {
      setProcessing(campaign.id);
      await apiCall(`/api/admin/marketing/ad-approvals/${campaign.id}/request-edit`, {
        method: 'POST',
        body: JSON.stringify({
          feedback: editFeedback,
          required_changes: requiredChanges.split('\n').filter(c => c.trim()),
        }),
      });
      toast({
        title: 'Edit Request Sent',
        description: 'Vendor has been notified of required changes',
      });
      setEditDialog({ open: false, campaign: null });
      setEditFeedback('');
      setRequiredChanges('');
      fetchApprovals();
    } catch (error) {
      console.error('Failed to request edit:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to send edit request',
        variant: 'destructive',
      });
    } finally {
      setProcessing(null);
    }
  };

  const formatTimeRemaining = (hours) => {
    if (hours < 0) {
      return `${Math.abs(Math.floor(hours))}h overdue`;
    }
    if (hours < 1) {
      return `${Math.floor(hours * 60)}m remaining`;
    }
    return `${Math.floor(hours)}h ${Math.floor((hours % 1) * 60)}m remaining`;
  };

  const getSLABadge = (campaign) => {
    if (campaign.is_overdue) {
      return <Badge variant="destructive" className="animate-pulse">⚠️ OVERDUE</Badge>;
    }
    if (campaign.hours_remaining < 6) {
      return <Badge variant="destructive">🔴 {formatTimeRemaining(campaign.hours_remaining)}</Badge>;
    }
    if (campaign.hours_remaining < 24) {
      return <Badge variant="warning" className="bg-yellow-500">🟡 {formatTimeRemaining(campaign.hours_remaining)}</Badge>;
    }
    return <Badge variant="outline">🟢 {formatTimeRemaining(campaign.hours_remaining)}</Badge>;
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold">Ad Approvals</h1>
            <p className="text-muted-foreground">
              Review and approve vendor advertising campaigns (48-hour SLA)
            </p>
          </div>
          <Button onClick={fetchApprovals} variant="outline" disabled={loading}>
            <RefreshCcw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Pending Review</p>
                  <p className="text-3xl font-bold">{approvals.pending_count}</p>
                </div>
                <Clock className="h-10 w-10 text-muted-foreground" />
              </div>
            </CardContent>
          </Card>
          <Card className={approvals.overdue_count > 0 ? 'border-red-500' : ''}>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Overdue (SLA Breach)</p>
                  <p className="text-3xl font-bold text-red-600">{approvals.overdue_count}</p>
                </div>
                <AlertTriangle className="h-10 w-10 text-red-500" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">SLA Target</p>
                  <p className="text-3xl font-bold">48h</p>
                </div>
                <CheckCircle className="h-10 w-10 text-green-500" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filter */}
        <div className="flex items-center gap-4">
          <Label>Filter by status:</Label>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-48">
              <SelectValue placeholder="Select status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Pending</SelectItem>
              <SelectItem value="pending_review">Pending Review</SelectItem>
              <SelectItem value="needs_revision">Needs Revision</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Approval Queue */}
        <Card>
          <CardHeader>
            <CardTitle>Approval Queue</CardTitle>
            <CardDescription>
              Campaigns awaiting review, sorted by urgency
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-10">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : approvals.campaigns.length === 0 ? (
              <div className="text-center py-10 text-muted-foreground">
                <CheckCircle className="h-12 w-12 mx-auto mb-4 text-green-500" />
                <p>All caught up! No campaigns pending review.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {approvals.campaigns.map((campaign) => (
                  <div
                    key={campaign.id}
                    className={`border rounded-lg p-4 ${campaign.is_overdue ? 'border-red-300 bg-red-50' : ''}`}
                  >
                    <div className="flex justify-between items-start gap-4">
                      <div className="flex-1 space-y-2">
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold">{campaign.campaign_name}</h3>
                          <Badge variant="outline">{campaign.campaign_type}</Badge>
                          {getSLABadge(campaign)}
                        </div>
                        <div className="flex items-center gap-4 text-sm text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <User className="h-4 w-4" />
                            {campaign.vendor_name}
                          </span>
                          <span className="flex items-center gap-1">
                            <DollarSign className="h-4 w-4" />
                            ₹{campaign.budget}
                          </span>
                        </div>
                        {campaign.creative_url && (
                          <div className="flex items-center gap-2">
                            <ImageIcon className="h-4 w-4 text-muted-foreground" />
                            <a
                              href={campaign.creative_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-sm text-blue-600 hover:underline"
                            >
                              View Creative
                            </a>
                          </div>
                        )}
                        {campaign.creative_content && (
                          <p className="text-sm bg-gray-50 p-2 rounded">
                            {campaign.creative_content.substring(0, 150)}
                            {campaign.creative_content.length > 150 && '...'}
                          </p>
                        )}
                      </div>
                      <div className="flex flex-col gap-2">
                        <Button
                          size="sm"
                          onClick={() => setApproveConfirm({ open: true, campaign })}
                          disabled={processing === campaign.id}
                        >
                          <CheckCircle className="h-4 w-4 mr-1" />
                          Approve
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setEditDialog({ open: true, campaign })}
                          disabled={processing === campaign.id}
                        >
                          <Edit3 className="h-4 w-4 mr-1" />
                          Request Edit
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => setRejectDialog({ open: true, campaign })}
                          disabled={processing === campaign.id}
                        >
                          <XCircle className="h-4 w-4 mr-1" />
                          Reject
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Approve Confirmation */}
      <AlertDialog
        open={approveConfirm.open}
        onOpenChange={(open) => setApproveConfirm({ open, campaign: null })}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Approve Campaign?</AlertDialogTitle>
            <AlertDialogDescription>
              This will approve &quot;{approveConfirm.campaign?.campaign_name}&quot; and set it to active/scheduled.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => handleApprove(approveConfirm.campaign)}
              disabled={processing}
            >
              {processing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Approve
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Reject Dialog */}
      <Dialog open={rejectDialog.open} onOpenChange={(open) => setRejectDialog({ open, campaign: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject Campaign</DialogTitle>
            <DialogDescription>
              Rejecting &quot;{rejectDialog.campaign?.campaign_name}&quot; will refund ₹{rejectDialog.campaign?.budget} to the vendor.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="reason">Rejection Reason (required)</Label>
              <Input
                id="reason"
                placeholder="e.g., Inappropriate content, misleading claims"
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="feedback">Additional Feedback (optional)</Label>
              <Textarea
                id="feedback"
                placeholder="Provide detailed feedback to help the vendor improve..."
                value={rejectFeedback}
                onChange={(e) => setRejectFeedback(e.target.value)}
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectDialog({ open: false, campaign: null })}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleReject} disabled={processing}>
              {processing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Reject & Refund
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Request Dialog */}
      <Dialog open={editDialog.open} onOpenChange={(open) => setEditDialog({ open, campaign: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Request Campaign Edits</DialogTitle>
            <DialogDescription>
              Request changes to &quot;{editDialog.campaign?.campaign_name}&quot; without rejecting it.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="editFeedback">Feedback (required)</Label>
              <Textarea
                id="editFeedback"
                placeholder="Explain what changes are needed..."
                value={editFeedback}
                onChange={(e) => setEditFeedback(e.target.value)}
                rows={3}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="changes">Required Changes (one per line)</Label>
              <Textarea
                id="changes"
                placeholder="Change headline text&#10;Update image resolution&#10;Add disclaimer"
                value={requiredChanges}
                onChange={(e) => setRequiredChanges(e.target.value)}
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialog({ open: false, campaign: null })}>
              Cancel
            </Button>
            <Button onClick={handleRequestEdit} disabled={processing}>
              {processing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Send Edit Request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

export default AdApprovalsPage;
